<template>
  <div>
    <div id="call-records-view" class="page-table scrollable">
      <el-dialog
        width="440px"
        :visible="modalVisible"
        :before-close="handleCloseModal"
        :close-on-click-modal="false"
      >
        <h2 class="form-section-header">Filter Settings</h2>

        <h3 class="black-text mb-5">Select Date Range</h3>
        <el-date-picker
          class="mb-10"
          style="margin-left: auto"
          v-model="historyDateRange"
          type="datetimerange"
          align="right"
          unlink-panels
          format="yyyy-MM-dd HH:mm"
          :clearable="false"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions"
        >
        </el-date-picker>

        <el-form id="stats-advanced-modal" class="form-modal">
          <h3 class="black-text mb-0">Filter By</h3>
          <el-select
            v-model="primaryFilter"
            placeholder="Select Filter"
            clearable
            style="margin-top: 0"
          >
            <template slot="prefix"> Filter By </template>
            <el-option
              v-for="{ label, value } in filterByOptions"
              :key="label"
              :label="label"
              :value="value"
            ></el-option>
          </el-select>

          <template v-if="primaryFilter">
            <div
              v-if="primaryFilter === 'mac'"
              class="el-input el-input--prefix"
            >
              <input
                type="text"
                autocomplete="off"
                placeholder="Enter Mac Address"
                class="el-input__inner"
                :value="mac"
                @input="mac = $event.target.value.toUpperCase()"
                v-mask="'NN:NN:NN:NN:NN:NN'"
              />
              <span class="el-input__prefix">Mac Address</span>
            </div>

            <el-input
              v-if="primaryFilter === 'ip'"
              v-model="ip"
              placeholder="Enter IP Address"
            >
              <template slot="prefix">IP Address</template>
            </el-input></template
          >

          <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
        </el-form>
      </el-dialog>

      <div id="users-stats-view" class="no-table-box page-header">
        <div class="flex justify-space-between mb-20">
          <h2 class="mb-0 mt-0 text-left">
            <v-svg sprite="profile" class="voxo-yellow"></v-svg>
            Provision Logs
          </h2>
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total Provisions: {{ total }}
          </el-tag>
        </div>

        <div class="flex align-center mb-20">
          <el-radio-group
            v-model="presetSelection"
            @change="doPreset"
            class="hidden-xs-only"
          >
            <el-radio-button type="default" label="yesterday"
              >Yesterday</el-radio-button
            >
            <el-radio-button type="default" label="last10"
              >Last 10 Days</el-radio-button
            >
            <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
          </el-radio-group>

          <el-tooltip placement="top" :content="selectedDateRange">
            <el-button @click="modalVisible = true" class="ml-10">
              Filter
            </el-button>
          </el-tooltip>
          <el-button
            @click="resetTable"
            v-if="advancedActive"
            style="background-color: #f25a6b; color: #fff"
          >
            Reset
          </el-button>
          <el-button class="ml-10" @click="doExport"> Export </el-button>
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="150"
            :pager-count="5"
            :total="total"
            v-if="total > limit"
            style="margin-left: auto"
          />
        </div>
        <div class="vxe-table-height card-base card-shadow--medium">
          <vxe-grid
            round
            class="user-stats-table"
            border="inner"
            ref="xGrid"
            height="100%"
            auto-resize
            empty-text="No Data"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :data="tableData"
            :loading="loadingData"
            :columns="tableColumns"
          >
          </vxe-grid>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { ExportToCsv } from "export-to-csv";

export default {
  name: "ProvisionLog",

  data() {
    return {
      loadingData: false,
      modalVisible: false,
      tableData: [],
      presetSelection: "last10",
      advancedActive: false,
      exportCSV: false,
      page: 1,
      pages: 0,
      limit: 150,
      total: 0,

      // ⭐️ Filter ⭐️ //
      filterByOptions: [
        {
          label: "Mac Address",
          value: "mac",
        },
        {
          label: "IP Address",
          value: "ip",
        },
      ],
      primaryFilter: "",
      mac: "",
      ip: "",
      startDate: null,
      endDate: null,
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },

      // ⭐️ Table Columns ⭐️ //
      tableColumns: [
        {
          field: "time",
          title: "Time",
          width: "150",
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [
                <span class="first-table-column">
                  {this.timeFormatter(row.time)}
                </span>,
              ];
            },
          },
        },
        {
          field: "requestType",
          title: "Type",
          width: "120",
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.requestType}</span>];
            },
          },
        },
        {
          field: "mac",
          title: "MAC",
          width: "150",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.mac}</span>];
            },
          },
        },
        {
          field: "status",
          title: "Status",
          width: "100",
          sortable: true,
        },
        {
          field: "ip",
          title: "IP Address",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.ip}</span>];
            },
          },
        },
        {
          field: "userAgent",
          title: "User Agent",
          minWidth: "200",
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.userAgent}</span>];
            },
          },
        },
        {
          field: "reason",
          title: "Fail Reason",
          minWidth: "200",
          showOverflow: true,
          sortable: true,
        },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    userOffsetMinutes() {
      return this.$store.state.userTimeOffset;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY HH:mm");
        const dateEnd = moment(this.endDate).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },

  watch: {
    page: debounce(function () {
      this.queryStats();
    }, 500),

    selectedTenant() {
      this.resetTable();
    },

    primaryFilter() {
      this.mac = "";
      this.ip = "";
    },
  },

  methods: {
    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    pageSet(page) {
      this.page = page;
    },

    handleCloseModal() {
      this.modalVisible = false;
    },

    doPreset(val) {
      if (val === "init") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.advancedActive = true;
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        console.log(this.endDate);
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD HH:mm");
        console.log(this.startDate);
        this.presetSelection = "lastMonth";
        this.advancedActive = true;
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.endDate = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.advancedActive = true;
        this.updateDateRangePicker();
        this.queryStats();
      }
    },

    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.endDate = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.updateDateRangePicker();
      this.queryStats();
      this.handleCloseModal();
      this.presetSelection = null;
    },

    resetTable() {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.mac = "";
      this.ip = "";
      this.exportCSV = false;
      this.page = 1;
      this.doPreset("init");
    },

    queryStats() {
      this.loadingData = true;
      feathersClient
        .service("provision-logs")
        .find({
          query: {
            type: "provisionLogReport",
            startDate: this.startDate,
            endDate: this.endDate,
            primaryFilter: this.primaryFilter,
            mac: this.mac,
            ip: this.ip,
            tenantId: this.selectedTenant.id,
            $limit: this.limit,
            timezone: this.user.timezone,
            exportCSV: this.exportCSV,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
          },
        })
        .then((res) => {
          //update table if not exporting
          if (!this.exportCSV && res && res.data) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
          }
          this.exportCSV = false;
          this.loadingData = false;
        })
        .catch(() => {
          this.tableData = [];
          this.loadingData = false;
        });
    },

    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },

    timeFormatterExport(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("YY/MM/DD hh:mm:ss A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("YY/MM/DD hh:mm:ss A");
    },

    timeFormatter(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD h:mm:ss A")
        : moment(value).add(offsetMinutes, "minutes").format("M/DD h:mm:ss A");
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} users?`, {
        type: "warning",
      })
        .then(() => {
          if (this.pages === 1) {
            //export from browser
            let csvData = [];
            for (let i = 0; i < this.tableData.length; i++) {
              const csvObj = {};
              let exclusionList = ["_XID"];
              for (let key in this.tableData[i]) {
                if (exclusionList.includes(key) === false) {
                  csvObj[key] = this.tableData[i][key]
                    ? this.tableData[i][key]
                    : "";
                }
              }
              csvData.push(csvObj);
            }

            const options = {
              filename: "provision_log_records",
              showLabels: true,
              headers: [
                "Time",
                "Request Type",
                "Mac",
                "Status",
                "Reason",
                "IP",
                "UserAgent",
              ],
            };

            let csvRecorder = csvData.map((obj) => {
              let reorderObj = {};
              reorderObj["time"] = this.timeFormatterExport(obj.start);
              reorderObj["requestType"] = obj.requestType;
              reorderObj["mac"] = obj.mac;
              reorderObj["status"] = obj.status;
              reorderObj["reason"] = obj.reason;
              reorderObj["ip"] = obj.ip;

              return reorderObj;
            });

            csvData.push(csvRecorder);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvRecorder);
          } else {
            this.exportCSV = true;
            this.queryProvisionLogRecords();
          }
        })
        .catch(() => true);
    },

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },

    queryProvisionLogRecords() {
      this.loadingData = true;

      feathersClient
        .service("provision-logs")
        .find({
          query: {
            type: "provisionLogReport",
            startDate: this.startDate,
            endDate: this.endDate,
            mac: this.mac,
            ip: this.ip,
            tenantId: this.selectedTenant.id,
            exportCSV: this.exportCSV,
            primaryFilter: this.primaryFilter,
            $sort: { time: -1 },
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
          },
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.limit = res.limit;
            this.pages = Math.ceil(res.total / res.limit);
            this.total = res.total;
            this.page = this.page > this.pages ? 1 : this.page;
            this.tableData = res.data;
          }

          if (this.exportCSV) {
            if (!res.url) {
              this.$notify({
                title: "Not Found",
                message: "File Not Found.",
                type: "warning",
              });
            } else {
              this.downloadCSV(res);
            }
          }

          this.exportCSV = false;
          this.loadingData = false;
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
  },

  mounted() {
    this.doPreset("init");
  },
};
</script>
